import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import { trackInternalEvent } from '@rio/tracking'
import { useParams } from 'react-router-dom'

import { Optional } from 'packages/types'
import { CoverLetterEditor } from 'builder/components/DocumentEditor'
import PreviewPanel, { useDocumentDownload } from 'builder/components/PreviewPanel'
import { DocumentCustomizationModalSwitcher } from 'builder/components/DocumentCustomizationModal'
import FloatingPreviewButton from 'builder/components/FloatingPreviewButton'

import { actions, selectors } from 'builder/modules/coverLetterEditor'
import { actions as generalEditorActions } from 'builder/modules/generalEditor'
import { CoverLetterRegenerationStatus, DocumentTypes } from 'builder/modules/constants'
import { navigate } from 'builder/modules/navigate'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useEffectOnMount } from 'builder/hooks/useEffectOnMount'
import PreviewReloadService from 'builder/services/PreviewReloadService'
import { Container } from './styles'

export const CoverLetterEditorView = () => {
  const editorState = useTypedSelector(state => state.generalEditor)
  const coverLetterState = useTypedSelector(state => state.coverLetterEditor)
  const isOnline = useTypedSelector(state => state.application.isOnline)
  const isContentAnimationPending = useTypedSelector(selectors.isContentAnimationPending)
  const fallbackLocale = useTypedSelector(
    state => state.application.configs.country?.locale || 'en',
  )
  const isAPremiumUser = useTypedSelector(state => state.user.data?.hasPremiumFeatures || false)
  const aiClFreeAttempts = useTypedSelector(state => state.user.data?.aiClFreeAttempts || 0)
  const isDownloading = useTypedSelector(state => state.rendering.isDocumentDownloading)
  const params = useParams()
  const mediaQueries = useMediaQueries()
  const dispatch = useDispatch()
  const [isFullScreen, setIsFullScreen] = useState(false)

  const { coverLetter, openEditorEventTracked, error, isSyncing } = coverLetterState
  const coverLetterId = parseInt(params.id || '')
  const isDataLoaded = coverLetter && editorState.isLoaded
  const currentDocument = !coverLetter
    ? null
    : isContentAnimationPending
    ? { ...coverLetter, content: '' }
    : coverLetter
  const { handleDownloadDocument } = useDocumentDownload({
    currentDocument,
    downloadClickEventSource: `full_screen_cover_letter_preview`,
    downloadActionEventSource: 'builder_full_screen_preview',
  })
  const hasContent = !!currentDocument?.content

  useEffectOnMount(() => {
    dispatch(actions.fetchCoverLetterRequest({ id: coverLetterId }))
    dispatch(generalEditorActions.fetchEditorPayloadRequest())
  })

  useEffect(() => {
    if (!hasContent) return
    PreviewReloadService.reloadResume(currentDocument, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasContent])

  useEffect(() => {
    if (error) navigate('/cover-letters')

    if (coverLetter && !openEditorEventTracked) {
      // track the page opening
      trackInternalEvent('visit_cover_letter_editor', {
        cover_letter_id: coverLetterId,
        ...(coverLetter.aiGenerated ? { label: 'ai_generated' } : {}),
      })
      dispatch(actions.setOpenEditorEventTracked(true))
    }
  }, [dispatch, coverLetter, coverLetterId, error, openEditorEventTracked])

  const handleFullSizeOpen = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault()
    setIsFullScreen(true)
  }, [])

  const handleFloatingButtonClick = useCallback(() => {
    setIsFullScreen(true)
    trackInternalEvent('open_full_screen_cover_letter_preview')
  }, [])

  const handleFullSizeClose = useCallback(() => {
    setIsFullScreen(false)
  }, [])

  const handleSimpleFieldUpdate = useCallback(
    (name: any, value: any, debounce?: boolean | number) => {
      dispatch(actions.updateSimpleField({ name, value, debounce }))

      if (name === 'name') trackInternalEvent('rename_cover_letter')
      if (name === 'locale') trackInternalEvent('change_cover_letter_locale', { locale: value })
      if (name === 'template')
        trackInternalEvent('change_cover_letter_template', { template: value })
    },
    [dispatch],
  )

  const handleTemplateUpdate = useCallback(
    (id: string) => {
      handleSimpleFieldUpdate('template', id)
    },
    [handleSimpleFieldUpdate],
  )

  const resetAIRegenerationStatus = useCallback(() => {
    dispatch(actions.setAIRegenerationStatus(CoverLetterRegenerationStatus.notStarted))
  }, [dispatch])

  const setShowConfirmRegenerateModal = useCallback(
    (value: boolean) => {
      dispatch(actions.setShowConfirmRegenerateModal(value))
    },
    [dispatch],
  )

  const setShowAICoverLetterPaywall = useCallback(() => {
    dispatch(actions.setShowAICoverLetterPaywall())
  }, [dispatch])

  const handleColorSelect = useCallback(
    (color: Optional<string>) => {
      dispatch(
        actions.updateSimpleField({
          name: 'color',
          value: color,
          debounce: false,
        }),
      )
    },
    [dispatch],
  )

  return (
    <Container>
      <CoverLetterEditor
        {...{
          editorState,
          coverLetterState,
          aiClFreeAttempts,
          isAPremiumUser,
          isContentAnimationPending,
          setShowAICoverLetterPaywall,
          fallbackLocale,
          setShowConfirmRegenerateModal,
          coverLetterId,
          resetAIRegenerationStatus,
          isOnline,
        }}
        updateSimpleField={handleSimpleFieldUpdate}
      />

      {/* Prevent rendering if the block isn't visible (reduce the load of user's device) */}
      {!isFullScreen && mediaQueries.isDesktop && currentDocument && (
        <PreviewPanel
          onFullSize={handleFullSizeOpen}
          type={DocumentTypes.coverLetter}
          documentId={coverLetterId}
          isSyncing={isSyncing}
          isDownloading={isDownloading}
          currentDocument={currentDocument}
          isFullScreen={isFullScreen}
          isOnline={isOnline}
        />
      )}

      {isFullScreen && currentDocument && (
        <DocumentCustomizationModalSwitcher
          type={DocumentTypes.coverLetter}
          isSyncing={isSyncing}
          isDownloading={isDownloading}
          currentDocument={currentDocument}
          editorState={editorState}
          onClose={handleFullSizeClose}
          handleDownloadDocument={handleDownloadDocument}
          updateTemplate={handleTemplateUpdate}
          handleColorSelect={handleColorSelect}
        />
      )}

      {mediaQueries.isLaptop && isDataLoaded && (
        <FloatingPreviewButton isSaving={isSyncing} onClick={handleFloatingButtonClick} />
      )}
    </Container>
  )
}
